import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import PageHeader from "@/components/app/PageHeader";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X } from "lucide-react";

/**
 * Generic admin table + edit modal for a `{id, code, description}` collection.
 * Used by both /system/container-types and /system/material-types.
 */
export default function RefDataAdmin({ kind, title, subtitle, codeLabel = "Code", descLabel = "Description" }) {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null); // null | 'new' | row object
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/${kind}`);
      setRows(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setLoading(false); }
  }, [kind]);

  useEffect(() => { refresh(); }, [refresh]);

  const remove = async (row) => {
    if (!window.confirm(`Delete "${row.code}"? Any records referencing this code will keep the value but the dropdown option will disappear.`)) return;
    try {
      await api.delete(`/${kind}/${row.id}`);
      toast.success(`${row.code} deleted`);
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div>
      <PageHeader
        title={title}
        subtitle={subtitle}
        actions={
          <button
            onClick={() => setEditing("new")}
            data-testid={`${kind}-new-btn`}
            className="flex items-center gap-1.5 px-3 py-2 bg-orange-500 hover:bg-orange-600 text-white text-[11px] uppercase tracking-widest font-semibold"
          >
            <Plus className="w-3.5 h-3.5" /> New
          </button>
        }
      />
      <div className="p-8">
        <div className="bg-white border border-gray-200 overflow-x-auto" data-testid={`${kind}-table`}>
          <table className="w-full text-sm">
            <thead className="bg-gray-900 text-white">
              <tr>
                <th className="text-left px-4 py-2.5 text-[10px] uppercase tracking-widest font-semibold w-48">{codeLabel}</th>
                <th className="text-left px-4 py-2.5 text-[10px] uppercase tracking-widest font-semibold">{descLabel}</th>
                <th className="text-right px-4 py-2.5 text-[10px] uppercase tracking-widest font-semibold w-24">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan="3" className="text-center py-8 text-gray-500">Loading…</td></tr>}
              {!loading && rows.length === 0 && <tr><td colSpan="3" className="text-center py-10 text-gray-500">No entries. Click New to add one.</td></tr>}
              {!loading && rows.map((r) => (
                <tr key={r.id} className="odd:bg-gray-50 border-b border-gray-100" data-testid={`${kind}-row-${r.code}`}>
                  <td className="px-4 py-2 font-mono text-xs text-gray-900">{r.code}</td>
                  <td className="px-4 py-2 text-sm text-gray-800">{r.description || <span className="text-gray-400">—</span>}</td>
                  <td className="px-4 py-2 text-right">
                    <button onClick={() => setEditing(r)} data-testid={`${kind}-edit-${r.code}`} className="p-1.5 text-gray-500 hover:text-gray-900 hover:bg-gray-100">
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => remove(r)} data-testid={`${kind}-delete-${r.code}`} className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <EditDialog
          kind={kind}
          codeLabel={codeLabel}
          descLabel={descLabel}
          initial={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); refresh(); }}
        />
      )}
    </div>
  );
}

function EditDialog({ kind, codeLabel, descLabel, initial, onClose, onSaved }) {
  const [code, setCode] = useState(initial?.code || "");
  const [description, setDescription] = useState(initial?.description || "");
  const [busy, setBusy] = useState(false);
  const isEdit = !!initial;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (isEdit) await api.patch(`/${kind}/${initial.id}`, { code, description });
      else await api.post(`/${kind}`, { code, description });
      toast.success(isEdit ? "Updated" : "Added");
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white border border-gray-300 w-full max-w-md">
        <div className="border-b border-gray-200 px-5 py-4 flex items-center justify-between">
          <div className="font-display font-bold text-xl">{isEdit ? "Edit" : "New"} Entry</div>
          <button onClick={onClose} data-testid={`${kind}-close-btn`}><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <div>
            <label className="label-caps block mb-1.5">{codeLabel}</label>
            <input required value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} className="w-full py-2 px-3 border border-gray-300 text-sm font-mono" data-testid={`${kind}-code-input`} placeholder="e.g. DUMP" />
            <div className="text-[11px] text-gray-500 mt-1">Short unique identifier. Auto-uppercased.</div>
          </div>
          <div>
            <label className="label-caps block mb-1.5">{descLabel}</label>
            <input required value={description} onChange={(e) => setDescription(e.target.value)} className="w-full py-2 px-3 border border-gray-300 text-sm" data-testid={`${kind}-desc-input`} placeholder="e.g. Dump Truck" />
          </div>
          <div className="flex gap-3 pt-3">
            <button type="button" onClick={onClose} className="flex-1 border border-gray-300 py-2.5 text-xs uppercase tracking-widest font-semibold">Cancel</button>
            <button type="submit" disabled={busy} data-testid={`${kind}-save-btn`} className="flex-1 bg-orange-500 hover:bg-orange-600 text-white py-2.5 text-xs uppercase tracking-widest font-semibold disabled:opacity-50">{isEdit ? "Update" : "Save"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
